import * as yup from 'yup'
import { parse, isValid, isFuture, isWithinInterval } from 'date-fns'
import { isValidIBAN } from 'ibantools'

declare module 'yup' {
  interface StringSchema {
    dateValid(errorMessage: string): StringSchema
    notFutureDate(errorMessage: string): StringSchema
    inDateRange(start: Date, end: Date, errorMessage: string): StringSchema
    iban(errorMessage: string): StringSchema
    isValidName(errorMessage: string): StringSchema
  }
}

yup.addMethod<yup.StringSchema>(yup.string, 'dateValid', function dateValid(errorMessage: string) {
  return this.test('dateValid', errorMessage, function test(value?: string) {
    const { path, createError } = this

    if (!value) {
      return createError({ path, message: errorMessage })
    }

    // Check if the year is 4 characters
    const year = value.split('/')[2]
    if (year && year.length !== 4) {
      return createError({ path, message: errorMessage })
    }

    // Use date-fns to validate the date format
    const parsedDate = parse(value, 'dd/MM/yyyy', new Date())
    if (isValid(parsedDate)) {
      return true // valid date
    }

    // If not valid, return a yup error
    return createError({ path, message: errorMessage })
  })
})

yup.addMethod<yup.StringSchema>(yup.string, 'notFutureDate', function notFutureDate(errorMessage: string) {
  return this.test('notFutureDate', errorMessage, function test(value?: string) {
    const { path, createError } = this

    if (!value) {
      return createError({ path, message: errorMessage })
    }

    // Use date-fns to validate the date format
    const parsedDate = parse(value, 'dd/MM/yyyy', new Date())

    if (!isFuture(parsedDate)) {
      return true // valid date
    }

    // If not valid, return a yup error
    return createError({ path, message: errorMessage })
  })
})

yup.addMethod<yup.StringSchema>(
  yup.string,
  'inDateRange',
  function inDateRange(start: Date, end: Date, errorMessage: string) {
    return this.test('inDateRange', errorMessage, function test(value?: string) {
      const { path, createError } = this

      if (!value) {
        return createError({ path, message: errorMessage })
      }

      // Use date-fns to validate the date format
      const parsedDate = parse(value, 'dd/MM/yyyy', new Date())

      // Check if the date is within the range
      if (isWithinInterval(parsedDate, { start, end })) {
        return true
      }

      // If not valid, return a yup error
      return createError({ path, message: errorMessage })
    })
  }
)

yup.addMethod<yup.StringSchema>(yup.string, 'iban', function iban(errorMessage: string) {
  return this.test('iban', errorMessage, (value: string | null | undefined) => {
    if (typeof value !== 'string') return false
    return isValidIBAN(value)
  })
})

yup.addMethod<yup.StringSchema>(yup.string, 'isValidName', function isValidName(errorMessage: string) {
  return this.test('isValidName', errorMessage, function test(value?: string) {
    const { path, createError } = this

    if (!value) {
      return createError({ path, message: errorMessage })
    }

    // Allows for alphbetical characters
    // and non-Latin alphabetical characters ([\p{L}] is a Unicode property that matches any letter in any language)
    // and hyphens and apostrophes
    const regex = /^[\p{L}'-]+ [\p{L}'-]+( [\p{L}'-]+)*$/u

    return regex.test(value)
  })
})
